import { useMemo } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom-v5-compat';

import { CloneCaseTemplateFormData } from 'dashboard/forms/FormCloneCaseTemplate';
import { NewCaseTemplateFormData } from 'dashboard/forms/FormNewCaseTemplate';
import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { generateDefaultCaseConfiguration } from 'dashboard/lib/constants/case-template';
import { MessageType } from 'dashboard/lib/interfaces/Message';
import { productFeatureDisabled } from 'dashboard/lib/productFeature';
import { CaseTemplate, CaseTemplateStatus } from 'dashboard/models/case-template';
import { ProductFeatureName } from 'dashboard/models/organization';
import {
  setCurrentPage,
  setFilters,
  setPageSize,
  setSortParams,
} from 'dashboard/redux/actions/case-templates';
import { showMessage } from 'dashboard/redux/actions/messages';
import { RootState } from 'dashboard/redux/store';
import { loadCaseTemplatesList } from 'dashboard/redux/thunk-actions/case-templates';
import { loadAllRoles } from 'dashboard/redux/thunk-actions/roles';
import DashboardCaseTemplatesView from 'dashboard/views/DashboardCaseTemplatesView';
import ApiResponseError from 'lib/errors/ApiResponseError';
import useApiCallback from 'lib/hooks/useApiCallback';
import useApiErrorHandler from 'lib/hooks/useApiErrorHandler';
import useApiLoader from 'lib/hooks/useApiLoader';
import useTable from 'lib/hooks/useTable';
import { ApiEndpoint, ApiModel } from 'lib/interfaces/api';
import { isPermissionsError, permissionsErrorMessage } from 'lib/permissions-errors';

const DashboardCaseTemplatesRoute = (props: PropsFromRedux) => {
  const navigate = useNavigate();

  const productFeatureIsDisabled = productFeatureDisabled(
    props.organization,
    ProductFeatureName.CasesDashboard,
    props.currentEnvironment.type,
  );

  const abortController = useMemo(
    () => new AbortController(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      props.loadCaseTemplatesList,
      props.currentEnvironment,
      props.currentPage,
      props.pageSize,
      props.sortParams,
      props.filters,
    ],
  );

  const { loading: loadingCases, callback: caseTemplateLoader } = useApiLoader({
    loader: () =>
      props.loadCaseTemplatesList({
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        currentPage: props.currentPage,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        pageSize: props.pageSize,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        sortParams: props.sortParams,
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        filters: props.filters,
        signal: abortController.signal,
      }),
    deps: [
      props.loadCaseTemplatesList,
      props.currentPage,
      props.pageSize,
      props.sortParams,
      props.filters,
    ],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load case templates.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  useApiLoader({
    loader: () => props.loadAllRoles(),
    deps: [props.loadAllRoles],
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage('Failed to load roles.', MessageType.Failure);
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: createCaseTemplateError, callback: createCaseTemplate } = useApiCallback(
    async ({ name, 'case-prefix': casePrefix, 'role-ids': roleIds }: NewCaseTemplateFormData) => {
      try {
        const response = await SessionClientDashboard.postResourceCreate<CaseTemplate>(
          {
            attributes: {
              name: name,
              'case-prefix': casePrefix,
              configuration: generateDefaultCaseConfiguration(),
            },
            type: ApiModel.CaseTemplate,
          },
          {
            meta: { 'role-ids': roleIds },
          },
        );

        props.showMessage('Successfully created case template.', MessageType.Success, true);
        navigate(`/dashboard/case-templates/${response.data.id}`);
      } catch (err) {
        if (err instanceof ApiResponseError && err.status === 409) {
          props.showMessage(
            'There is already a case template with this name or key',
            MessageType.Failure,
          );
        } else {
          throw err;
        }
      }
    },
    [caseTemplateLoader],
  );

  useApiErrorHandler({
    error: createCaseTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to create case template, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'We are experiencing network issues. Please try again.',
        MessageType.Failure,
      ),
  });

  const { error: archiveCaseTemplateError, callback: archiveCaseTemplate } = useApiCallback(
    async (caseTemplate: CaseTemplate) => {
      await SessionClientDashboard.deleteResource<CaseTemplate>(
        ApiEndpoint.CaseTemplate,
        caseTemplate.id,
      );
      caseTemplateLoader();
      props.showMessage('Successfully archived case template.', MessageType.Success);
    },
    [caseTemplateLoader],
  );

  useApiErrorHandler({
    error: archiveCaseTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to archive case template, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Failed to archive case template, please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const { error: restoreCaseTemplateError, callback: restoreCaseTemplate } = useApiCallback(
    async (caseTemplate: CaseTemplate) => {
      await SessionClientDashboard.postResourceAction<CaseTemplate>(
        {
          type: ApiModel.CaseTemplate,
          id: caseTemplate.id,
        },
        'restore',
      );
      caseTemplateLoader();
      props.showMessage('Successfully restored case template.', MessageType.Success);
    },
    [caseTemplateLoader],
  );

  useApiErrorHandler({
    error: restoreCaseTemplateError,
    onError: (error) => {
      if (isPermissionsError(error)) {
        props.showMessage(permissionsErrorMessage(error), MessageType.Failure);
      } else {
        props.showMessage(
          'Failed to restore case template, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Failed to restore case template, please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const { error: cloneCaseTemplateError, callback: cloneCaseTemplate } = useApiCallback(
    async (
      token: string,
      { name, 'case-prefix': casePrefix, 'role-ids': roleIds }: CloneCaseTemplateFormData,
    ) => {
      await SessionClientDashboard.postResourceAction<CaseTemplate>(
        {
          type: ApiModel.CaseTemplate,
          id: token,
          attributes: {
            name,
            'case-prefix': casePrefix,
          },
        },
        'clone',
        {
          meta: { 'role-ids': roleIds },
        },
      );
      caseTemplateLoader();
      props.showMessage('Successfully cloned case template.', MessageType.Success, true);
    },
    [caseTemplateLoader],
  );

  useApiErrorHandler({
    error: cloneCaseTemplateError,
    onError: (err) => {
      if (isPermissionsError(err)) {
        props.showMessage(permissionsErrorMessage(err), MessageType.Failure);
      } else if (err instanceof ApiResponseError && err.status === 409) {
        props.showMessage(
          'There is already a case template with this name or key',
          MessageType.Failure,
        );
      } else {
        props.showMessage(
          'Failed to clone case template, please try again.',
          MessageType.Failure,
          false,
        );
      }
    },
    onNetworkError: () =>
      props.showMessage(
        'Failed to clone case template, please try again.',
        MessageType.Failure,
        false,
      ),
  });

  const { handlePageLinkClick, handlePageSizeClick, handleSortUpdate } = useTable({
    filters: props.filters,
    sortParams: props.sortParams,
    pageSize: props.pageSize,
    currentPage: props.currentPage,
    setFilters: props.setFilters,
    setCurrentPage: props.setCurrentPage,
    setPageSize: props.setPageSize,
    setSortParams: props.setSortParams,
  });

  const handleStatusFilterUpdate = (status: CaseTemplateStatus[]) => {
    props.setFilters({ status: status });
    handlePageLinkClick(1);
  };

  const handleOmniSearch = (omni: string) => {
    props.setFilters({ omni: omni });
    handlePageLinkClick(1);
  };

  const hasCasesFree = !productFeatureDisabled(
    props.organization,
    ProductFeatureName.CasesFree,
    props.currentEnvironment.type,
  );
  const hasCasesBasic = !productFeatureDisabled(
    props.organization,
    ProductFeatureName.CasesBasic,
    props.currentEnvironment.type,
  );
  const hasCasesEnhanced = !productFeatureDisabled(
    props.organization,
    ProductFeatureName.CasesEnhanced,
    props.currentEnvironment.type,
  );
  const hasCasesEnterprise = !productFeatureDisabled(
    props.organization,
    ProductFeatureName.CasesEnterprise,
    props.currentEnvironment.type,
  );

  return (
    <DashboardCaseTemplatesView
      currentEnvironment={props.currentEnvironment}
      roles={props.roles}
      isLoading={loadingCases}
      productFeatureIsDisabled={productFeatureIsDisabled}
      hasCasesFree={hasCasesFree}
      hasCasesBasic={hasCasesBasic}
      hasCasesEnhanced={hasCasesEnhanced}
      hasCasesEnterprise={hasCasesEnterprise}
      atActiveCaseTemplateLimit={props.atActiveCaseTemplateLimit}
      caseTemplates={props.caseTemplates}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      paginationParams={props.paginationParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      sortParams={props.sortParams}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      filters={props.filters}
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      pageSize={props.pageSize}
      handlePageLinkClick={handlePageLinkClick}
      handlePageSizeClick={handlePageSizeClick}
      handleSortUpdate={handleSortUpdate}
      handleStatusFilterUpdate={handleStatusFilterUpdate}
      handleOmniSearch={handleOmniSearch}
      createCaseTemplate={createCaseTemplate}
      archiveCaseTemplate={archiveCaseTemplate}
      restoreCaseTemplate={restoreCaseTemplate}
      cloneCaseTemplate={cloneCaseTemplate}
    />
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    currentEnvironment: state.currentEnvironment.currentEnvironment,
    caseTemplates: state.caseTemplates.caseTemplates,
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    roles: state.roles.allRoles,
    paginationParams: state.caseTemplates.paginationParams,
    sortParams: state.caseTemplates.sortParams,
    filters: state.caseTemplates.filters,
    currentPage: state.caseTemplates.currentPage,
    pageSize: state.caseTemplates.pageSize,
    atActiveCaseTemplateLimit: state.caseTemplates.atActiveCaseTemplateLimit,
    organization: state.organization.organization,
  };
};
const mapDispatchToProps = {
  loadCaseTemplatesList,
  loadAllRoles,
  setSortParams,
  setFilters,
  setCurrentPage,
  setPageSize,
  showMessage,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(DashboardCaseTemplatesRoute);
